import type { ClassifiedOption, SpreadGroup } from "@/lib/classify-positions";
import { occIdentity } from "@/lib/parse-utils";

function synthId(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h === 0 ? -1 : -Math.abs(h);
}

/** Open-table row from a live broker spread that is not yet in option_trades. */
export function tradeFromBrokerSpread<T>(sp: SpreadGroup, accountHash: string): T {
  const isPut = sp.putCall === "PUT";
  const contracts = Math.min(sp.shortLeg.contracts, sp.longLeg.contracts) || sp.shortLeg.contracts;
  const shortPremium = Math.abs(sp.shortLeg.averagePrice);
  const longPremium = Math.abs(sp.longLeg.averagePrice);
  return {
    id: synthId(occIdentity(sp.shortLeg.optionSymbol) + "|" + occIdentity(sp.longLeg.optionSymbol)),
    accountHash,
    symbol: sp.underlying,
    spreadType: isPut ? "bull_put" : "bear_call",
    spreadWidth: sp.width,
    stockPriceAtEntry: 0,
    shortOptionSymbol: sp.shortLeg.optionSymbol,
    longOptionSymbol: sp.longLeg.optionSymbol,
    contracts,
    shortStrikePrice: sp.shortLeg.strike,
    longStrikePrice: sp.longLeg.strike,
    expirationDate: sp.expiration,
    dteAtEntry: 0,
    openedAt: new Date().toISOString(),
    shortPremium,
    longPremium,
    netPremium: shortPremium - longPremium,
    entryIv: null,
    entryProbItm: null,
    entryMarketCap: null,
    entryAvgDailyVolume: null,
    entryHvRank: null,
    entryHvPct: null,
    entryHvDte: null,
    entryOpenInterest: null,
    entryPremiumPct: null,
    closedAt: null,
    exitType: null,
    exitPrice: null,
    shortExitPrice: null,
    longExitPrice: null,
    stockPriceAtExit: null,
    exitIv: null,
    exitProbItm: null,
    exitHvPct: null,
    exitHvDte: null,
    exitPremiumPct: null,
    realizedPnl: null,
    premiumYieldPct: null,
    entryOrderId: null,
    exitOrderId: null,
    takeProfitOrderId: null,
    status: "open",
  } as T;
}

/** DB open spreads, plus live broker spreads that have not been ingested yet. */
export function mergeBrokerOpenSpreads<T extends { status: string; symbol: string; shortOptionSymbol: string }>(
  fromDb: T[],
  spreads: SpreadGroup[],
  isIndex: (underlying: string) => boolean,
  accountHash: string,
): T[] {
  const opens = fromDb.filter((t) => t.status === "open" && isIndex(t.symbol));
  const covered = new Set(opens.map((t) => occIdentity(t.shortOptionSymbol)));
  const extra: T[] = [];
  for (const sp of spreads) {
    if (!isIndex(sp.underlying)) continue;
    if (covered.has(occIdentity(sp.shortLeg.optionSymbol))) continue;
    extra.push(tradeFromBrokerSpread<T>(sp, accountHash));
  }
  return extra.length ? [...opens, ...extra] : opens;
}

/** The ledger fields a spread is rebuilt from. */
interface SpreadTrade {
  symbol: string;
  spreadType: string;
  spreadWidth: number;
  expirationDate: string;
  contracts: number;
  shortOptionSymbol: string;
  shortStrikePrice: number;
  shortPremium: number;
  longOptionSymbol: string;
  longStrikePrice: number;
  longPremium: number;
}

/** Close from the trade's own OCC legs. Never borrow a RUTW order for a RUT row. */
export function spreadFromTrade<T extends SpreadTrade>(t: T): SpreadGroup & { trade: T } {
  const isPut = t.spreadType === "bull_put";
  const pc = isPut ? "PUT" : "CALL";
  const leg = (optionSymbol: string, strike: number, isShort: boolean, avg: number): ClassifiedOption => ({
    optionSymbol, underlying: t.symbol, putCall: pc, description: "",
    strike, expiration: t.expirationDate, contracts: t.contracts,
    isShort, averagePrice: avg, marketValue: 0,
  });
  return {
    underlying: t.symbol,
    putCall: pc,
    expiration: t.expirationDate,
    shortLeg: leg(t.shortOptionSymbol, t.shortStrikePrice, true, t.shortPremium),
    longLeg: leg(t.longOptionSymbol, t.longStrikePrice, false, t.longPremium),
    width: t.spreadWidth,
    trade: t,
  };
}
